import { Injectable } from '@angular/core';
import Utils from '../../../app/shared/utils/utils';
import { GameElementTypeConfigOptions } from '../../../config/game-element-type-config.interface';
import { GameElementCategory } from '../../game/enum/game-element-category.enum';
import { FormControlOptions } from '../interfaces/form-control-options.interface';
import { FormControlSelectOptions } from '../interfaces/form-control-select-options.interface';
import { Dictionary } from '../models/dictionary';
import { GameUtilHelper } from '../models/helper/game-util-helper';
import { ConfigurationService } from '../services/configuration.service';
import { GameResultTypeUtils } from './game-result-type-utils';
import { GameScoringSystemTypeUtils } from './game-scoring-system-type-utils';
import { GameSelectionTypeUtils } from './game-selection-type-utils';
import { GameSolutionTypeUtils } from './game-solution-type-utils';

@Injectable({
  providedIn: 'root'
})
export class GameElementTypeUtils {

  private elementTypeValues;

  constructor(
    private readonly config: ConfigurationService,
    private readonly selectionTypeUtils: GameSelectionTypeUtils,
    private readonly solutionTypeUtils: GameSolutionTypeUtils,
    private readonly resultTypeUtils: GameResultTypeUtils,
    private readonly scoringSystemTypeUtils: GameScoringSystemTypeUtils,
    private readonly gameUtilHelper: GameUtilHelper
  ) {
  }

  private getElementTypes(): GameElementTypeConfigOptions {
    if (this.config.isLoadingConfig()) {
      return undefined;
    }

    const config = this.config.getGameElementTypeConfig();
    if (config) {
      return Utils.clone(config);
    }

    return undefined;
  }

  getTypeConfig(type: number) {
    return this.getElementTypes()[ type ];
  }

  getLabel(elementType: string | number) {
    if (this.getElementTypes() && this.isValid(elementType)) {
      return this.getElementTypes()[ Number(elementType) ].label;
    }

    return '';
  }

  hasField(elementType: string | number, field: string): boolean {
    if (this.getElementTypes()) {
      return Object.keys(this.getElementTypes()[ Number(elementType) ].fields).indexOf(field) >= 0;
    }

    return false;
  }

  getFieldOptions(elementType: string | number, field: string, defaultOptions: FormControlOptions<any>): FormControlOptions<any> {
    if (this.getElementTypes()) {
      const options = this.hasField(elementType, field) ? Utils.clone(this.getElementTypes()[ Number(elementType) ].fields[ field ]) : {};

      for (const optionName in defaultOptions) {
        if (Utils.isDefined(defaultOptions[ optionName ])) {
          options[ optionName ] = Utils.isDefined(options[ optionName ]) ? options[ optionName ] : defaultOptions[ optionName ];
        }
      }

      options.key = defaultOptions.key || field;
      options.hidden = Utils.isDefined(defaultOptions.hidden) ? defaultOptions.hidden : !this.hasField(elementType, field);

      return options;
    }

    return {};
  }

  getTypeValues(): number[] {
    if (this.getElementTypes()) {
      this.elementTypeValues = this.elementTypeValues || Object.keys(this.getElementTypes())
        .filter(Number)
        .map(elementType => Number(elementType));

      return Utils.clone(this.elementTypeValues);
    }

    return [];
  }

  getCategoryOfType(type: number): string {
    const config = this.getElementTypes()[ type ];

    return config && config.category;
  }

  getCategoryValues(): string[] {
    return Object.values(GameElementCategory);
  }

  getDefaultType(): number {
    return this.getTypeValues()[ 0 ];
  }

  getDefaultCategory(): string {
    return this.getCategoryValues()[ 0 ];
  }

  isValid(elementType: string | number): boolean {
    return !isNaN(Number(elementType)) && this.getTypeValues().indexOf(Number(elementType)) >= 0;
  }

  getTypeSelectOptions(): FormControlSelectOptions<number> {
    return this.getTypeValues()
      .map(elementType => {
        return {
          value: elementType,
          label: this.getLabel(elementType)
        };
      });
  }

  getCategorySelectOptions(): FormControlSelectOptions<string> {
    return this.getCategoryValues()
      .map(value => ({ value, label: value.toUpperCase() }));
  }

  getModules(elementType: string | number): string[] {
    if (this.getElementTypes()) {
      return Utils.clone(this.getElementTypes()[ Number(elementType) ].modules);
    }

    return [];
  }

  isModuleEnabled(elementType: string | number, module: string): boolean {
    return this.getModules(elementType).indexOf(module) >= 0;
  }

  getSelectionTypes(elementType: string | number): number[] {
    if (this.getElementTypes()) {
      const selectionTypes = this.getElementTypes()[ Number(elementType) ].selectionTypes;
      return (selectionTypes === 'all' ? this.selectionTypeUtils.getTypeValues() : selectionTypes) || [];
    }

    return [];
  }

  getSolutionTypes(elementType: string | number): number[] {
    if (this.getElementTypes()) {
      const solutionTypes = this.getElementTypes()[ Number(elementType) ].solutionTypes;
      return (solutionTypes === 'all' ? this.solutionTypeUtils.getTypeValues() : solutionTypes) || [];
    }

    return [];
  }

  getResultTypes(elementType: string | number): number[] {
    if (this.getElementTypes()) {
      const resultTypes = this.getElementTypes()[ Number(elementType) ].resultTypes;
      return (resultTypes === 'all' ? this.resultTypeUtils.getTypeValues() : resultTypes) || [];
    }

    return [];
  }

  getScoringSystemType(elementType: string | number): number {
    if (this.getElementTypes()) {
      return this.getElementTypes()[ Number(elementType) ].scoringSystem;
    }

    return undefined;
  }

  createDictionary(levelType: string | number, key: 'parameters' | 'textDictionary' | 'assetDictionary'): any[] {

    const types = this.getElementTypes();

    if (levelType && types) {
      const dict: Dictionary<any> = types[ Number(levelType) ];

      return this.gameUtilHelper.createDictionary(dict, key);
    }

    return [];
  }
}
